/**
 * Verträge der Chart-Validierung (AP3.T3.4).
 *
 * Die Chart-Daten aus T3.3 sind ungeprüfte Modellausgabe. Hier entscheidet
 * sich, ob sie zur Wahrheit werden dürfen — das ist die Gegenmaßnahme zu
 * Risiko R2 aus dem Gesamtscope.
 *
 * **Drei unabhängige Prüfungen.** Unabhängig heißt: Keine speist sich aus den
 * Daten, die sie prüft. Die Frequenzsumme rechnet innerhalb der Matrix, der
 * Caption-Abgleich hält sie gegen die Prozentwerte aus T3.1 (die kein Modell je
 * gesehen hat), die Plausibilität prüft die Form gegen Pokerwissen.
 */
/* -------------------------------------------------------------------------
 * Prüfarten und Befunde
 * ---------------------------------------------------------------------- */
/** Die drei Prüfarten. */
export const CHART_CHECKS = [
    'frequency-sum',
    'caption-match',
    'legend-match',
    'plausibility',
];
export function isChartCheck(value) {
    return typeof value === 'string' && CHART_CHECKS.includes(value);
}
/**
 * Schweregrad eines Befunds.
 *
 * - `error`   — verhindert `validated`. Das Chart bleibt `raw`.
 * - `warning` — Hinweis; blockiert nicht. Die Heuristiken aus Prüfung 3 melden
 *   auf dieser Stufe, weil sie Hinweisgeber sind und keine Beweise.
 * - `info`    — Sachverhalt ohne Schuldzuweisung, etwa „nicht prüfbar".
 */
export const CHART_FINDING_SEVERITIES = ['error', 'warning', 'info'];
/** Art eines Befunds. Jede Art gehört zu genau einer Prüfung. */
export const CHART_FINDING_KINDS = [
    // Prüfung 1
    'frequency-sum-off',
    // Prüfung 2
    'caption-mismatch',
    'caption-missing-action',
    'caption-not-checkable',
    'legend-mismatch',
    'legend-missing-action',
    'legend-not-checkable',
    // Prüfung 3
    'incomplete-matrix',
    'empty-cell',
    'monotonicity',
    'outlier',
];
/** Welche Prüfung eine Befundart erzeugt. */
export const FINDING_CHECK = {
    'frequency-sum-off': 'frequency-sum',
    'caption-mismatch': 'caption-match',
    'caption-missing-action': 'caption-match',
    'caption-not-checkable': 'caption-match',
    'legend-mismatch': 'legend-match',
    'legend-missing-action': 'legend-match',
    'legend-not-checkable': 'legend-match',
    'incomplete-matrix': 'plausibility',
    'empty-cell': 'plausibility',
    monotonicity: 'plausibility',
    outlier: 'plausibility',
};
/* -------------------------------------------------------------------------
 * Toleranzen
 * ---------------------------------------------------------------------- */
/**
 * Toleranzen der Prüfungen. Bewusst **vorab** festgelegt und begründet
 * (ADR-0034) — sie werden nicht nachträglich an ein Ergebnis angepasst.
 */
export const CHART_TOLERANCES = {
    /**
     * Frequenzsumme je Zelle in Prozentpunkten.
     *
     * Das Buch rundet seine Anteile; ein Chart mit drei Aktionen zu je einer
     * Nachkommastelle kann legitim 99,9 oder 100,1 ergeben. 2,0 pp lässt Raum
     * für gerundete Drittel (33,3 × 3 = 99,9) und für die Schätzung von
     * Flächenanteilen bei Mischzellen, ohne echte Lesefehler durchzulassen —
     * eine vergessene Aktion verschiebt die Summe um ein Vielfaches davon.
     */
    frequencySumPp: 2.0,
    /**
     * Combo-gewichteter Abgleich gegen die Caption-Prozente in Prozentpunkten.
     * Der Wert ist von der AP-Datei vorgegeben.
     */
    captionMatchPp: 1.5,
    /**
     * Combo-gewichteter Abgleich gegen die **im Bild gedruckte Legende** in
     * Prozentpunkten (AP3.T3.6-fix).
     *
     * Derselbe Wert wie beim Caption-Abgleich, und aus demselben Grund: Der
     * Messfehler sitzt nicht im Vergleichswert, sondern in der Matrix. Die
     * gedruckte Legende ist gesetzter Text mit zwei Nachkommastellen
     * (`59.65 %`) — Ziffern abzulesen ist etwas anderes, als einen Farbanteil zu
     * schätzen. Die Unsicherheit stammt also allein aus der combo-gewichteten
     * Summe über 169 Zellen, und für die hat T3.4 bereits 1,5 pp begründet.
     *
     * Am Bestand geprüft: 15 der 21 automatisch bestandenen Charts trafen ihren
     * gedruckten Wert auf ≤ 0,09 pp, HR 16 nach der Korrektur auf 0,53 pp — alle
     * weit innerhalb. Die fünf nachweislich falsch gelesenen Charts lagen
     * zwischen 1,81 und 11,39 pp und werden damit alle erkannt.
     */
    legendMatchPp: 1.5,
    /**
     * Monotonie: Um wie viele Prozentpunkte darf eine **dominierte** Hand
     * aggressiver gespielt werden als die dominierende, bevor es auffällt.
     * Großzügig, weil echte Strategien lokale Ausnahmen kennen (Blocker,
     * Playability); 25 pp trifft vertauschte Zeilen, nicht Feinheiten.
     */
    monotonicityPp: 25,
    /**
     * Ausreißer: Abweichung einer Zelle vom Mittel ihrer Rasternachbarn, ab der
     * gemeldet wird — und nur, wenn die Nachbarn untereinander einig sind.
     */
    outlierPp: 60,
};
/** Alle Prüfungen an. */
export const ALL_CHECKS = {
    frequencySum: true,
    captionMatch: true,
    legendMatch: true,
    completeness: true,
    monotonicity: true,
    outlier: true,
};
/* -------------------------------------------------------------------------
 * Herkunft einer Zelle
 * ---------------------------------------------------------------------- */
/**
 * Woher der Wert einer Zelle stammt.
 *
 * `manual` ist gegen jedes automatische Überschreiben geschützt — weder ein
 * erneuter Validierungslauf noch ein Zweitdurchlauf fasst solche Zellen an.
 */
export const CHART_CELL_SOURCES = ['model', 'manual'];
export function isChartErrorResponse(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value.error === 'invalid_chart' &&
        Array.isArray(value.fields));
}
//# sourceMappingURL=validation.js.map